import { PlanType } from "@brookbush/database";
import {
	type PlanConfig,
	type PlanConfigExpanded,
	TrialLength,
} from "@brookbush/types";

import { useUser } from "@clerk/nextjs";
import { useRouter } from "next/compat/router";
import { useEffect, useRef } from "react";
import type Stripe from "stripe";
import { create } from "zustand";

import useAccess from "./useAccess";
import usePayPalSubscription from "./usePayPalSubscription";
import usePromoCode from "./usePromoCode";
import useSale from "./useSale";
import useStripePrices from "./useStripePrices";
import useStripeSubscription from "./useStripeSubscription";
import {
	planConfigsEqual,
	reconcileMembershipPriceId,
	resolveDefaultMembershipPrice,
} from "~/lib/membership-plan-defaults";

interface PlanConfigState {
	planConfigValue: PlanConfig;
	setPlanConfigValue: (planConfig?: PlanConfig) => void;
}

const DEFAULT_PLAN_CONFIG: PlanConfig = {
	planType: PlanType.INDIVIDUAL,
};

const usePlanConfigStore = create<PlanConfigState>((set) => ({
	planConfigValue: DEFAULT_PLAN_CONFIG,
	setPlanConfigValue: (planConfigValue = DEFAULT_PLAN_CONFIG) =>
		set({ planConfigValue }),
}));

const usePlanConfig = () => {
	const { planConfigValue, setPlanConfigValue } = usePlanConfigStore();

	const { prices, isLoadingPrices } = useStripePrices(planConfigValue.planType);
	const { stripeSubscription } = useStripeSubscription();
	const { paypalSubscription } = usePayPalSubscription();
	const { promoCode, isLoadingPromoCode } = usePromoCode();
	const { isMember, isFormerMember, isLoadingAccess } = useAccess();
	const { sale } = useSale();
	const { user, isLoaded } = useUser();

	const router = useRouter();
	const appliedLookupKey = useRef<string | undefined>(undefined);
	const query = (router?.query ?? {}) as {
		trial?: string;
		type?: string;
		key?: string;
		quantity?: string;
	};

	const price = prices?.find((p) => p.id === planConfigValue.priceId);

	const isLoadingPlanConfig =
		isLoadingPromoCode ||
		isLoadingPrices ||
		!planConfigValue.priceId ||
		!planConfigValue.currency;

	const isTrialEligible = !isMember && !isFormerMember;

	useEffect(() => {
		if (
			isLoadingPlanConfig ||
			(!Object.values(query).length && !promoCode) ||
			isLoadingAccess ||
			(isMember && !sale?.isAnnualUpgrade)
		) {
			return;
		}

		const updatedPlanConfigValue: PlanConfig = { ...planConfigValue };

		if (promoCode && updatedPlanConfigValue.promoCodeId !== promoCode.id) {
			updatedPlanConfigValue.promoCodeId = promoCode.id;
		}

		if (
			isTrialEligible &&
			query.trial &&
			Object.values(TrialLength).includes(query.trial as TrialLength) &&
			updatedPlanConfigValue.trialLength !== query.trial
		) {
			updatedPlanConfigValue.trialLength = query.trial as TrialLength;
		}

		if (!isTrialEligible && updatedPlanConfigValue.trialLength) {
			updatedPlanConfigValue.trialLength = undefined;
		}

		const planTypeParam = query.type?.toUpperCase() as PlanType | undefined;

		if (
			planTypeParam &&
			Object.values(PlanType).includes(planTypeParam) &&
			updatedPlanConfigValue.planType !== planTypeParam
		) {
			updatedPlanConfigValue.planType = planTypeParam;

			if (updatedPlanConfigValue.planType === PlanType.INDIVIDUAL) {
				(updatedPlanConfigValue as any).quantity = undefined;
				updatedPlanConfigValue.priceId = undefined;
			} else {
				updatedPlanConfigValue.quantity = 2;
				updatedPlanConfigValue.priceId = undefined;
			}
		}

		if (
			query.quantity &&
			+query.quantity >= 2 &&
			updatedPlanConfigValue.planType === PlanType.TEAM &&
			updatedPlanConfigValue.quantity !== +query.quantity
		) {
			updatedPlanConfigValue.quantity = +query.quantity;
		}

		const priceFromQueryKey = prices?.find((p) => p.lookup_key === query.key);

		if (
			query.key &&
			priceFromQueryKey &&
			appliedLookupKey.current !== query.key
		) {
			updatedPlanConfigValue.priceId = priceFromQueryKey.id;
			appliedLookupKey.current = query.key;
		}

		if (planConfigsEqual(planConfigValue, updatedPlanConfigValue)) return;

		setPlanConfigValue(updatedPlanConfigValue);
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [isLoadingPlanConfig, prices, promoCode, query, setPlanConfigValue]);

	useEffect(() => {
		const priceId = reconcileMembershipPriceId(prices, planConfigValue.priceId);
		if (priceId === planConfigValue.priceId) return;

		setPlanConfigValue({
			...planConfigValue,
			priceId,
		});
	}, [planConfigValue, prices, setPlanConfigValue]);

	useEffect(() => {
		if (prices && !planConfigValue.priceId) {
			const interval =
				stripeSubscription?.items?.data?.[0]?.price.recurring?.interval ??
				paypalSubscription?.plan.billing_cycles[0].frequency.interval_unit.toLowerCase();

			const defaultPrice = resolveDefaultMembershipPrice(prices, interval);

			if (defaultPrice) {
				setPlanConfigValue({
					...planConfigValue,
					priceId: defaultPrice.id,
					currency: defaultPrice.currency,
				});
			}
		}
	}, [
		paypalSubscription?.plan.billing_cycles,
		planConfigValue,
		prices,
		setPlanConfigValue,
		stripeSubscription?.items?.data,
	]);

	useEffect(() => {
		if (
			isLoaded &&
			isTrialEligible &&
			user?.unsafeMetadata.isTrialUser &&
			!planConfigValue.trialLength
		) {
			setPlanConfigValue({
				...planConfigValue,
				trialLength: TrialLength.WEEK,
			});
		}
	}, [isLoaded, isTrialEligible, planConfigValue, setPlanConfigValue, user]);

	// Drop stale trial flags for members / former members (sticky isTrialUser
	// metadata and ?trial= query must not promise $0 when Stripe will charge).
	useEffect(() => {
		if (isLoadingAccess || isTrialEligible || !planConfigValue.trialLength) {
			return;
		}

		setPlanConfigValue({
			...planConfigValue,
			trialLength: undefined,
		});
	}, [isLoadingAccess, isTrialEligible, planConfigValue, setPlanConfigValue]);

	const planConfig: PlanConfigExpanded = {
		...planConfigValue,
		promoCode,
		price,
	};

	const setQuantity = (quantity: number) => {
		if (
			planConfigValue.planType === PlanType.TEAM &&
			quantity >= 2 &&
			quantity <= 36
		) {
			setPlanConfigValue({ ...planConfigValue, quantity });
		}
	};

	const setPrice = (price: Stripe.Price) => {
		setPlanConfigValue({
			...planConfigValue,
			priceId: price.id,
			currency: price.currency,
		});
	};

	const setPlanType = (planType: PlanType) => {
		if (planType === PlanType.TEAM) {
			setPlanConfigValue({
				...planConfigValue,
				planType,
				quantity: 2,
				priceId: undefined,
			});
		} else {
			setPlanConfigValue({
				...planConfigValue,
				planType,
				quantity: undefined,
				priceId: undefined,
			} as unknown as PlanConfig);
		}
	};

	return {
		isLoadingPlanConfig,
		planConfig,
		resetPlanConfig: () => setPlanConfigValue(),
		setPlanType,
		setPrice,
		setQuantity,
	};
};

export default usePlanConfig;
